import {
  type AwsConnector,
  LAMBDA_IMAGE,
  LAMBDA_SETTINGS,
  LOG_RETENTION_DAYS,
  POLICY_NAMES,
  isCrossAccount,
  renderKmsKeyComment,
  toFunctionDescription,
} from "./iac.ts";

/** The message the script fails with for a secret in another account, until
 * the customer sets the customer-managed key that encrypts it. */
const CROSS_ACCOUNT_KMS_KEY_REQUIRED =
  "The private key's secret is in another AWS account, so it must be encrypted with a customer-managed KMS key. Set KMS_KEY_ARN to that key's ARN, and allow the connector's role in the secret's resource policy and in the key's policy.";

/** An IAM policy document with `statements`. */
const toPolicyDocument = (statements: object[]) => ({
  Version: "2012-10-17",
  Statement: statements,
});

/** Assigns `value`, as JSON, to the shell variable `name`. The heredoc expands
 * the script's own `$` variables in it, and nothing else in JSON is special to
 * it. */
const renderJsonVariable = (name: string, value: object) =>
  `${name}=$(cat <<EOF
${JSON.stringify(value, undefined, 2)}
EOF
)`;

const LAMBDA_TRUST_POLICY = toPolicyDocument([
  {
    Effect: "Allow",
    Principal: { Service: "lambda.amazonaws.com" },
    Action: "sts:AssumeRole",
  },
]);

// The connector writes its own logs, and no others.
const LOGS_POLICY = toPolicyDocument([
  {
    Effect: "Allow",
    Action: ["logs:CreateLogStream", "logs:PutLogEvents"],
    Resource:
      "arn:aws:logs:$CONNECTOR_REGION:$ACCOUNT_ID:log-group:$LOG_GROUP:*",
  },
]);

// "??????" matches the six random characters that end the secret's ARN, so
// the connector gets this one secret and no other.
const READ_PRIVATE_KEY = {
  Sid: "ReadPrivateKey",
  Effect: "Allow",
  Action: "secretsmanager:GetSecretValue",
  Resource: "$PRIVATE_KEY_SECRET_PARTIAL_ARN-??????",
};

const DECRYPT_PRIVATE_KEY = {
  Sid: "DecryptPrivateKey",
  Effect: "Allow",
  Action: "kms:Decrypt",
  Resource: "$KMS_KEY_ARN",
  Condition: {
    StringEquals: {
      "kms:ViaService": "secretsmanager.$SECRETS_REGION.amazonaws.com",
    },
  },
};

const INVOKE_POLICY = toPolicyDocument([
  {
    Effect: "Allow",
    Action: "lambda:InvokeFunction",
    Resource: "$FUNCTION_ARN",
  },
]);

// The tag the script marks what it creates with, whose value is the
// connector's organization.
const OWNER_TAG = "P0GitHubRepositoriesConnector";

/** The message the script fails with for an existing resource, named by `$1`,
 * that it didn't create. */
const NOT_OWNED =
  "The $1 already exists, but this script didn't create it, so it may belong to something else, and the script won't change it. If it's this connector, deployed with the Terraform, update it with the Terraform.";

// How many times, five seconds apart, the script tries to create the function
// while Lambda can't yet assume its new role.
const CREATE_ATTEMPTS = 12;

/** Fails the script for a secret in another account until its KMS key is
 * set. */
const renderCrossAccountCheck = (connector: AwsConnector) =>
  isCrossAccount(connector)
    ? `
[[ -n $KMS_KEY_ARN ]] ||
  fail "${CROSS_ACCOUNT_KMS_KEY_REQUIRED}"
`
    : "";

/**
 * Renders the bash script that deploys one GitHub organization's connector,
 * for the customer to run in their own cloud in place of its Terraform (see
 * `renderAwsTerraform`).
 *
 * It deploys what the Terraform does, with the AWS CLI and Docker, and checks
 * what the Terraform's plan checks: the account it's signed in to, and the
 * KMS key that a secret in another account needs. It creates what's missing
 * and sets the rest as the Terraform would, so running it again repairs the
 * connector, or updates it to a new image. It runs on bash 3.2, which macOS
 * ships.
 *
 * Where the Terraform would fail to create a resource whose name is taken,
 * the script tags what it creates with the org, and refuses to change an
 * existing resource without that tag, before it changes anything.
 */
export const renderAwsShell = (connector: AwsConnector): string => {
  const {
    accountId,
    connectorName,
    connectorRegion,
    imageDigest,
    imageTag,
    org,
    p0RoleName,
    secret,
    secretsRegion,
  } = connector;
  const description = toFunctionDescription(org);

  return `#!/usr/bin/env bash
# P0's GitHub Repositories connector for ${org}, on AWS Lambda.
#
# This deploys what the connector's Terraform deploys, with the AWS CLI. Save it
# as a file and run it with bash, where Docker and the AWS CLI are installed and
# the AWS CLI is signed in to account ${accountId}. It copies P0's connector
# image from Docker Hub into an ECR repository, which Lambda requires.
#
# P0 runs one connector for each organization, so deploy it once, with this
# script or with the Terraform. Run this again to repair the connector, or to
# update it after changing the image below: it creates what's missing and sets
# the rest as below. It changes only what it created, which it tags, so it
# won't take over anything else that has the connector's name. Each
# organization needs its own connector name: the connector's role, repository
# and log group are named after it, and these names are unique in an AWS
# account.

set -euo pipefail
# The AWS CLI would otherwise page long output, and wait for input.
export AWS_PAGER=""

# The GitHub organization the connector serves, which tags what the script
# creates.
GITHUB_ORG="${org}"
# Where P0 invokes the connector. P0's installer has the same values.
ACCOUNT_ID="${accountId}"
CONNECTOR_NAME="${connectorName}"
CONNECTOR_REGION="${connectorRegion}"
# The secret that holds the GitHub App's private key, by its partial ARN: its
# ARN without the hyphen and six random characters that end it. P0 gives the
# connector this ARN. The connector can read this secret and no other.
PRIVATE_KEY_SECRET_PARTIAL_ARN="${secret.partialArn}"
SECRETS_REGION="${secretsRegion}"
${renderKmsKeyComment(connector)}
KMS_KEY_ARN=""
# How many days CloudWatch keeps the connector's logs.
LOG_RETENTION_DAYS=${LOG_RETENTION_DAYS}
# The role P0 assumes in this account, which invokes the connector.
P0_ROLE_NAME="${p0RoleName}"
# P0's connector image: its tag on Docker Hub, and the digest that tag resolves
# to. The script copies the image by its digest.
IMAGE_TAG="${imageTag}"
IMAGE_DIGEST="${imageDigest}"

fail() {
  echo "$*" >&2
  exit 1
}

for tool in aws docker; do
  command -v "$tool" >/dev/null || fail "Install $tool, then run this again."
done

# P0 derives the connector's ARN from the account, region and name above, so
# the connector must be deployed exactly there.
CALLER_ACCOUNT_ID=$(aws sts get-caller-identity \\
  --query Account \\
  --output text \\
  --region "$CONNECTOR_REGION")
[[ $CALLER_ACCOUNT_ID == "$ACCOUNT_ID" ]] ||
  fail "The AWS CLI is signed in to account $CALLER_ACCOUNT_ID. Sign it in to account $ACCOUNT_ID, then run this again."

# The key's ARN goes into the connector's policy as it is.
[[ -z $KMS_KEY_ARN || $KMS_KEY_ARN =~ ^arn:aws:kms:[a-z0-9-]+:[0-9]{12}:key/[A-Za-z0-9-]+$ ]] ||
  fail "Set KMS_KEY_ARN to a KMS key's ARN, as in arn:aws:kms:<region>:<account>:key/<key-id>."
${renderCrossAccountCheck(connector)}
REGISTRY="$ACCOUNT_ID.dkr.ecr.$CONNECTOR_REGION.amazonaws.com"
LOG_GROUP="/aws/lambda/$CONNECTOR_NAME"
ROLE_ARN="arn:aws:iam::$ACCOUNT_ID:role/$CONNECTOR_NAME"
FUNCTION_ARN="arn:aws:lambda:$CONNECTOR_REGION:$ACCOUNT_ID:function:$CONNECTOR_NAME"
OWNER_TAG="${OWNER_TAG}"

# Fails unless $2, the owner tag on the existing $1, is this connector's
# organization.
require_owned() {
  [[ $2 == "$GITHUB_ORG" ]] ||
    fail "${NOT_OWNED}"
}

# Whether the lookup "$@", after the error code $1 that it fails with when the
# resource is missing, finds it. Any other failure, such as a denied or
# throttled lookup, fails the script, so it never reads as missing.
exists() {
  local missing=$1 error
  shift
  if error=$("$@" 2>&1 >/dev/null); then return 0; fi
  [[ $error == *"($missing)"* ]] || fail "$error"
  return 1
}

echo "Checking what already has the name $CONNECTOR_NAME"
# Each check comes before any change, so finding something the script didn't
# create changes nothing.
REPOSITORY_EXISTS=""
if exists RepositoryNotFoundException aws ecr describe-repositories \\
  --repository-names "$CONNECTOR_NAME" \\
  --region "$CONNECTOR_REGION"; then
  REPOSITORY_EXISTS=1
  OWNER=$(aws ecr list-tags-for-resource \\
    --resource-arn "arn:aws:ecr:$CONNECTOR_REGION:$ACCOUNT_ID:repository/$CONNECTOR_NAME" \\
    --query "tags[?Key=='$OWNER_TAG'].Value | [0]" \\
    --output text \\
    --region "$CONNECTOR_REGION")
  require_owned "ECR repository $CONNECTOR_NAME" "$OWNER"
fi
ROLE_EXISTS=""
if exists NoSuchEntity aws iam get-role --role-name "$CONNECTOR_NAME"; then
  ROLE_EXISTS=1
  OWNER=$(aws iam list-role-tags \\
    --role-name "$CONNECTOR_NAME" \\
    --query "Tags[?Key=='$OWNER_TAG'].Value | [0]" \\
    --output text)
  require_owned "role $CONNECTOR_NAME" "$OWNER"
fi
LOG_GROUP_EXISTS=""
# Lists no log group when it's missing, rather than failing.
LOG_GROUPS=$(aws logs describe-log-groups \\
  --log-group-name-prefix "$LOG_GROUP" \\
  --query "logGroups[?logGroupName=='$LOG_GROUP'].logGroupName" \\
  --output text \\
  --region "$CONNECTOR_REGION")
if [[ -n $LOG_GROUPS ]]; then
  LOG_GROUP_EXISTS=1
  OWNER=$(aws logs list-tags-for-resource \\
    --resource-arn "arn:aws:logs:$CONNECTOR_REGION:$ACCOUNT_ID:log-group:$LOG_GROUP" \\
    --query "tags.$OWNER_TAG" \\
    --output text \\
    --region "$CONNECTOR_REGION")
  require_owned "log group $LOG_GROUP" "$OWNER"
fi
FUNCTION_EXISTS=""
if exists ResourceNotFoundException aws lambda get-function \\
  --function-name "$CONNECTOR_NAME" \\
  --region "$CONNECTOR_REGION"; then
  FUNCTION_EXISTS=1
  OWNER=$(aws lambda list-tags \\
    --resource "$FUNCTION_ARN" \\
    --query "Tags.$OWNER_TAG" \\
    --output text \\
    --region "$CONNECTOR_REGION")
  require_owned "Lambda function $CONNECTOR_NAME" "$OWNER"
fi

echo "Setting up the ECR repository $CONNECTOR_NAME"
# Tags can't be overwritten, so no one who can push to this repository can
# replace the image that runs with access to the key.
if [[ -n $REPOSITORY_EXISTS ]]; then
  aws ecr put-image-tag-mutability \\
    --repository-name "$CONNECTOR_NAME" \\
    --image-tag-mutability IMMUTABLE \\
    --region "$CONNECTOR_REGION" >/dev/null
  aws ecr put-image-scanning-configuration \\
    --repository-name "$CONNECTOR_NAME" \\
    --image-scanning-configuration scanOnPush=true \\
    --region "$CONNECTOR_REGION" >/dev/null
else
  aws ecr create-repository \\
    --repository-name "$CONNECTOR_NAME" \\
    --image-tag-mutability IMMUTABLE \\
    --image-scanning-configuration scanOnPush=true \\
    --tags "Key=$OWNER_TAG,Value=$GITHUB_ORG" \\
    --region "$CONNECTOR_REGION" >/dev/null
fi

echo "Setting up the role $CONNECTOR_NAME"
${renderJsonVariable("LAMBDA_TRUST_POLICY", LAMBDA_TRUST_POLICY)}
if [[ -n $ROLE_EXISTS ]]; then
  aws iam update-assume-role-policy \\
    --role-name "$CONNECTOR_NAME" \\
    --policy-document "$LAMBDA_TRUST_POLICY"
else
  aws iam create-role \\
    --role-name "$CONNECTOR_NAME" \\
    --assume-role-policy-document "$LAMBDA_TRUST_POLICY" \\
    --tags "Key=$OWNER_TAG,Value=$GITHUB_ORG" >/dev/null
fi

echo "Setting up the log group $LOG_GROUP"
# The connector's role can't create a log group, so its own must exist before
# it runs.
if [[ -z $LOG_GROUP_EXISTS ]]; then
  aws logs create-log-group \\
    --log-group-name "$LOG_GROUP" \\
    --tags "$OWNER_TAG=$GITHUB_ORG" \\
    --region "$CONNECTOR_REGION"
fi
aws logs put-retention-policy \\
  --log-group-name "$LOG_GROUP" \\
  --retention-in-days "$LOG_RETENTION_DAYS" \\
  --region "$CONNECTOR_REGION"

${renderJsonVariable("LOGS_POLICY", LOGS_POLICY)}
aws iam put-role-policy \\
  --role-name "$CONNECTOR_NAME" \\
  --policy-name ${POLICY_NAMES.logs} \\
  --policy-document "$LOGS_POLICY"

# The connector reads the GitHub App's private key, and nothing else.
if [[ -z $KMS_KEY_ARN ]]; then
  ${renderJsonVariable("PRIVATE_KEY_POLICY", toPolicyDocument([READ_PRIVATE_KEY]))}
else
  ${renderJsonVariable(
    "PRIVATE_KEY_POLICY",
    toPolicyDocument([READ_PRIVATE_KEY, DECRYPT_PRIVATE_KEY])
  )}
fi
aws iam put-role-policy \\
  --role-name "$CONNECTOR_NAME" \\
  --policy-name ${POLICY_NAMES.privateKey} \\
  --policy-document "$PRIVATE_KEY_POLICY"

echo "Copying P0's connector image into ECR"
# ECR's tags can't be overwritten, so each copy is tagged with the image's tag
# and the time it was copied: copying the image again, to roll back or after an
# interrupted run, pushes a new tag.
COPY_TAG="$IMAGE_TAG-$(date -u +%Y%m%d%H%M%S)"
SOURCE="${LAMBDA_IMAGE}@$IMAGE_DIGEST"
TARGET="$REGISTRY/$CONNECTOR_NAME:$COPY_TAG"
(
  # Signs Docker out of ECR at the end, so its login doesn't outlast the copy.
  # A failed sign-out doesn't fail the copy once the image is pushed.
  trap 'docker logout "$REGISTRY" || true' EXIT
  aws ecr get-login-password --region "$CONNECTOR_REGION" |
    docker login --username AWS --password-stdin "$REGISTRY"
  docker pull --platform linux/amd64 "$SOURCE"
  docker tag "$SOURCE" "$TARGET"
  docker push "$TARGET"
)
# Pulling one platform and pushing it gives ECR that image's own manifest, not
# the tag's multi-platform one, so the digest there can differ from the one
# pinned above. Lambda needs ECR's.
ECR_DIGEST=$(aws ecr describe-images \\
  --repository-name "$CONNECTOR_NAME" \\
  --image-ids imageTag="$COPY_TAG" \\
  --query "imageDetails[0].imageDigest" \\
  --output text \\
  --region "$CONNECTOR_REGION")
IMAGE_URI="$REGISTRY/$CONNECTOR_NAME@$ECR_DIGEST"

echo "Deploying the Lambda function $CONNECTOR_NAME"
# No VPC: the connector calls api.github.com over the internet. Updating
# detaches any VPC the function was given since.
if [[ -n $FUNCTION_EXISTS ]]; then
  aws lambda update-function-configuration \\
    --function-name "$CONNECTOR_NAME" \\
    --description "${description}" \\
    --role "$ROLE_ARN" \\
    --vpc-config "SubnetIds=[],SecurityGroupIds=[]" \\
    --memory-size ${LAMBDA_SETTINGS.memorySize} \\
    --timeout ${LAMBDA_SETTINGS.timeout} \\
    --region "$CONNECTOR_REGION" >/dev/null
  aws lambda wait function-updated \\
    --function-name "$CONNECTOR_NAME" \\
    --region "$CONNECTOR_REGION"
  aws lambda update-function-code \\
    --function-name "$CONNECTOR_NAME" \\
    --image-uri "$IMAGE_URI" \\
    --architectures ${LAMBDA_SETTINGS.architecture} \\
    --region "$CONNECTOR_REGION" >/dev/null
  aws lambda wait function-updated \\
    --function-name "$CONNECTOR_NAME" \\
    --region "$CONNECTOR_REGION"
else
  # Lambda can't assume a new role for the first few seconds.
  for attempt in {1..${CREATE_ATTEMPTS}}; do
    if ERROR=$(aws lambda create-function \\
      --function-name "$CONNECTOR_NAME" \\
      --description "${description}" \\
      --role "$ROLE_ARN" \\
      --package-type Image \\
      --code ImageUri="$IMAGE_URI" \\
      --architectures ${LAMBDA_SETTINGS.architecture} \\
      --memory-size ${LAMBDA_SETTINGS.memorySize} \\
      --timeout ${LAMBDA_SETTINGS.timeout} \\
      --tags "$OWNER_TAG=$GITHUB_ORG" \\
      --region "$CONNECTOR_REGION" 2>&1 >/dev/null); then
      break
    fi
    [[ $ERROR == *"cannot be assumed by Lambda"* && $attempt -lt ${CREATE_ATTEMPTS} ]] ||
      fail "$ERROR"
    sleep 5
  done
  aws lambda wait function-active \\
    --function-name "$CONNECTOR_NAME" \\
    --region "$CONNECTOR_REGION"
fi

echo "Letting P0 invoke the connector"
# P0 invokes the connector as its role in this account, and needs nothing else
# on it.
${renderJsonVariable("INVOKE_POLICY", INVOKE_POLICY)}
aws iam put-role-policy \\
  --role-name "$P0_ROLE_NAME" \\
  --policy-name "$CONNECTOR_NAME-invoke" \\
  --policy-document "$INVOKE_POLICY"

echo "Deployed the connector $CONNECTOR_NAME in account $ACCOUNT_ID, region $CONNECTOR_REGION."
`;
};
